import React from 'react';
import { X, Info, Mountain, TreePine, Compass, Database, Calculator, AlertTriangle } from 'lucide-react';

export const InfoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div 
        className="bg-zinc-950 border border-white/10 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="info-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-950 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Info className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-bold text-white">Notice & Méthodologie</h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
            data-testid="close-info-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Légende */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Mountain className="w-4 h-4 text-cyan-400" />
              </span>
              Légende des Spots
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                    <Mountain className="w-3 h-3 text-white" />
                  </div>
                  <span className="font-semibold text-amber-400">Point Haut</span>
                </div>
                <p className="text-zinc-400 text-sm">
                  Sommets, pics et collines extraits d'OpenStreetMap (tags: natural=peak, natural=hill)
                </p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                    <TreePine className="w-3 h-3 text-white" />
                  </div>
                  <span className="font-semibold text-emerald-400">Confort</span>
                </div>
                <p className="text-zinc-400 text-sm">
                  Aires de pique-nique et abris extraits d'OpenStreetMap (tags: tourism=picnic_site, leisure=picnic_table)
                </p>
              </div>
            </div>

            {/* Score colors */}
            <div className="mt-4 bg-zinc-900/50 rounded-lg p-4">
              <div className="text-zinc-500 uppercase text-xs tracking-widest mb-3">Échelle des Scores</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { range: '80-100', color: '#10b981', label: 'Excellent' },
                  { range: '70-79', color: '#84cc16', label: 'Très bon' },
                  { range: '60-69', color: '#f59e0b', label: 'Bon' },
                  { range: '< 60', color: '#94a3b8', label: 'Moyen' },
                ].map((item) => (
                  <div key={item.range} className="text-center">
                    <div
                      className="w-full h-3 rounded-full mb-1"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="font-mono text-xs text-zinc-300">{item.range}</span>
                    <div className="text-xs text-zinc-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Méthodologie de scoring */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Calculator className="w-4 h-4 text-cyan-400" />
              </span>
              Méthodologie de Scoring
            </h3>
            
            <div className="space-y-4">
              {/* Points Hauts */}
              <div className="bg-zinc-900/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mountain className="w-4 h-4 text-amber-500" />
                  <span className="font-semibold text-amber-400">HamScore - Points Hauts</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Altitude relative</span>
                    <span className="text-cyan-400 font-mono">40%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Dégagement LOS (8 directions)</span>
                    <span className="text-cyan-400 font-mono">45%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Silence radio (QRM)</span>
                    <span className="text-cyan-400 font-mono">15%</span>
                  </div>
                </div>
              </div>

              {/* Conforts */}
              <div className="bg-zinc-900/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TreePine className="w-4 h-4 text-emerald-500" />
                  <span className="font-semibold text-emerald-400">HamScore - Conforts</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Équipements (abri, eau, WC...)</span>
                    <span className="text-cyan-400 font-mono">35%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Accessibilité (route, parking)</span>
                    <span className="text-cyan-400 font-mono">30%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Dégagement LOS (8 directions)</span>
                    <span className="text-cyan-400 font-mono">35%</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Calcul LOS */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Compass className="w-4 h-4 text-cyan-400" />
              </span>
              Calcul du Dégagement (LOS)
            </h3>
            <div className="bg-zinc-900/50 rounded-lg p-4 text-sm text-zinc-300">
              <p className="mb-3">
                Le <strong className="text-cyan-400">Line of Sight (LOS)</strong> évalue le dégagement dans les 8 directions cardinales (N, NE, E, SE, S, SW, W, NW).
              </p>
              <ul className="space-y-2 list-disc list-inside text-zinc-400">
                <li>Analyse des points hauts voisins comme obstacles potentiels</li>
                <li>Calcul de l'angle d'élévation vers chaque obstacle</li>
                <li><span className="text-emerald-400">Angle négatif</span> = horizon dégagé (direction libre)</li>
                <li><span className="text-red-400">Angle positif</span> = obstacle détecté (direction obstruée)</li>
                <li>Résultat exprimé en <strong>X/8 directions dégagées</strong></li>
              </ul>
            </div>
          </section>

          {/* Sources de données */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Database className="w-4 h-4 text-cyan-400" />
              </span>
              Sources de Données
            </h3>
            <div className="bg-zinc-900/50 rounded-lg p-4 text-sm">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></span>
                  <div>
                    <strong className="text-white">OpenStreetMap (OSM)</strong>
                    <p className="text-zinc-400">Base de données cartographique collaborative pour les points d'intérêt (sommets, aires de pique-nique)</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></span>
                  <div>
                    <strong className="text-white">Overpass API</strong>
                    <p className="text-zinc-400">Interface d'interrogation des données OSM en temps réel</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></span>
                  <div>
                    <strong className="text-white">Open-Elevation API</strong>
                    <p className="text-zinc-400">Service d'altitude basé sur les données SRTM (fallback sur tags OSM ele=*)</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Classement */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3">Méthodologie de Classement</h3>
            <div className="bg-zinc-900/50 rounded-lg p-4 text-sm text-zinc-300">
              <ol className="space-y-2 list-decimal list-inside">
                <li>Recherche de tous les spots dans le rayon défini</li>
                <li>Récupération des altitudes (tags OSM ou API)</li>
                <li>Calcul du HamScore pour chaque spot</li>
                <li>Tri par score décroissant</li>
                <li>Affichage des <strong className="text-cyan-400">5 meilleurs</strong> de chaque catégorie (minimum)</li>
              </ol>
            </div>
          </section>

          {/* Avertissement */}
          <section className="border-t border-white/10 pt-5">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-amber-400 font-semibold mb-1">Application Expérimentale</p>
                  <p className="text-zinc-400">
                    Cette application a été développée avec l'assistance de l'intelligence artificielle (IA). 
                    Les résultats sont fournis à titre indicatif et ne garantissent pas les conditions réelles sur le terrain. 
                    Vérifiez toujours l'accessibilité et la légalité des spots avant de vous y rendre.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-zinc-950 border-t border-white/10 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-md transition-colors"
          >
            Compris
          </button>
        </div>
      </div>
    </div>
  );
};
